import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { errMsg } from "../core/api";
import { useApp } from "../core/AppContext";
import { fa } from "../core/format";
import Modal from "./Modal";
import FileViewer from "./FileViewer";
import MultiFileField from "./MultiFileField";

const OK = ["application/pdf", "image/png", "image/jpeg", "image/webp"];
const MAX_MB = 5;
const sizeText = (b) =>
  b >= 1048576 ? `${fa((b / 1048576).toFixed(1))} مگابایت` : `${fa(Math.max(1, Math.round(b / 1024)))} کیلوبایت`;
const icon = (m) => (m === "application/pdf" ? "fa-regular fa-file-pdf" : m ? "fa-regular fa-image" : "fa-regular fa-file");

/* ---- کمکی‌های فرم (برای RecordPage) ---- */
export const attachmentsField = (label = "فایل‌ها / عکس‌ها (اختیاری)") => ({
  name: "attachments",
  label,
  type: "custom",
  noBody: true,
  full: true,
  render: ({ form, setForm, errors }) => (
    <MultiFileField
      label={label}
      files={form.attachments || []}
      error={errors.attachments}
      onChange={(v) => setForm((s) => ({ ...s, attachments: v }))}
    />
  ),
});
// اولین فایل به‌عنوان فایل اصلی همراه خود فرم ارسال می‌شود
export const mainFromAttachments = (fd, form) => {
  if (form.attachments?.length) fd.append("file", form.attachments[0]);
};
// بقیه‌ی فایل‌ها بعد از ثبت آپلود می‌شوند
export const afterSaveFor = (type) => async (data, form, { editing, row }) => {
  const extras = (form.attachments || []).slice(1);
  if (!extras.length) return;
  const id = editing ? row.id : data?.item?.id ?? data?.id;
  if (!id) throw new Error("no id");
  const fd = new FormData();
  extras.forEach((f) => fd.append("files", f));
  await api.post(`/equipment-files/${type}/${id}/files`, fd, { timeout: 180000 });
};

/* ---- مودال مشاهده و مدیریت فایل‌ها ---- */
export default function EntityFiles({ type, id, title, mainPath, canAdd, canRemove, onClose, onChanged }) {
  const { toast, confirm } = useApp();
  const inputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [viewer, setViewer] = useState(null);
  const base = `/equipment-files/${type}/${id}/files`;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(base);
      setItems(data.items);
    } catch (e) {
      toast(errMsg(e), "err");
    } finally {
      setLoading(false);
    }
  }, [base, toast]);
  useEffect(() => { load(); }, [load]);

  const onFiles = async (e) => {
    const picked = [...(e.target.files || [])];
    e.target.value = "";
    if (!picked.length) return;
    const bad = picked.find((f) => !OK.includes(f.type) || f.size > MAX_MB * 1024 * 1024);
    if (bad) return toast(`«${bad.name}»: فقط PDF یا تصویر تا ${fa(MAX_MB)} مگابایت مجاز است`, "err");
    setBusy(true);
    try {
      const fd = new FormData();
      picked.forEach((f) => fd.append("files", f));
      const { data } = await api.post(base, fd, { timeout: 180000 });
      setItems(data.items);
      toast("فایل‌ها ذخیره شد");
      onChanged?.();
    } catch (er) {
      toast(errMsg(er), "err");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (f) => {
    if (!(await confirm(`فایل «${f.name}» حذف شود؟`))) return;
    try {
      await api.delete(`${base}/${f.id}`);
      setItems((l) => l.filter((x) => x.id !== f.id));
      onChanged?.();
    } catch (er) {
      toast(errMsg(er), "err");
    }
  };

  const row = (key, name, mime, size, path, f) => (
    <div key={key} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid var(--border)", borderRadius: 10, padding: "8px 12px", background: "var(--card)" }}>
      <i className={icon(mime)} style={{ color: "var(--primary)" }} />
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={name}>{name}</span>
      {size ? <small className="hint" style={{ margin: 0 }}>{sizeText(size)}</small> : null}
      <button type="button" className="icon-btn" title="مشاهده" onClick={() => setViewer({ path, name })}><i className="fa-regular fa-eye" /></button>
      {f && canRemove && <button type="button" className="icon-btn danger" title="حذف" onClick={() => remove(f)}><i className="fa-solid fa-trash" /></button>}
    </div>
  );

  return (
    <>
      <Modal wide title={`فایل‌ها — ${title}`} onClose={() => !busy && !viewer && onClose()}
        footer={<button className="btn btn-ghost" onClick={onClose} disabled={busy}>بستن</button>}>
        {loading ? (
          <div className="empty"><div className="spinner" style={{ margin: "0 auto" }} /></div>
        ) : (
          <>
            <div className="box-head" style={{ marginBottom: 8 }}>
              <h3 style={{ fontSize: 14 }}>فایل‌ها و تصاویر پیوست</h3>
              {canAdd && (
                <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => inputRef.current.click()}>
                  <i className="fa-solid fa-plus" /> {busy ? "در حال ارسال..." : "افزودن فایل"}
                </button>
              )}
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              {mainPath && row("main", "فایل اصلی", "", 0, mainPath, null)}
              {items.map((f) => row(f.id, f.name, f.mime, f.size, `${base}/${f.id}`, f))}
              {!mainPath && !items.length && <div className="hint">فایلی ثبت نشده است.</div>}
            </div>
            <div className="hint" style={{ marginTop: 8 }}>
              تا {fa(10)} فایل اضافه، هر فایل حداکثر {fa(MAX_MB)} مگابایت (PDF یا تصویر)؛ انتخاب چندفایله مجاز است.
            </div>
            <input ref={inputRef} type="file" multiple hidden accept="application/pdf,image/png,image/jpeg,image/webp" onChange={onFiles} />
          </>
        )}
      </Modal>
      {viewer && <FileViewer {...viewer} onClose={() => setViewer(null)} />}
    </>
  );
}